import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * DivisionGallery — carousel auto-scroll per le foto di una divisione.
 *
 * Comportamento:
 *  - 1 immagine  → mostra la foto ferma senza controlli
 *  - 2+ immagini → carousel auto-play (5s per slide), pausa on-hover,
 *                  frecce prev/next, dots di navigazione, transizione crossfade
 *
 * Props:
 *  - images: [{ src, alt }]
 *  - intervalMs: durata slide (default 5000)
 *  - height: classe tailwind altezza contenitore (default 360)
 *  - testIdPrefix: base per data-testid
 */
export default function DivisionGallery({
  images = [],
  intervalMs = 5000,
  height = "h-[280px] sm:h-[400px]",
  testIdPrefix = "division-gallery",
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const count = images.length;

  const go = useCallback((next) => {
    if (count === 0) return;
    setIndex((prev) => (next + count) % count);
  }, [count]);

  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  // Auto-play
  useEffect(() => {
    if (paused || count <= 1) return undefined;
    timerRef.current = setTimeout(() => next(), intervalMs);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, count, intervalMs, next]);

  // Keyboard navigation quando la sezione è visibile
  useEffect(() => {
    if (count <= 1) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, next, prev]);

  if (count === 0) return null;

  // Singola immagine — no carousel, solo figure statica
  if (count === 1) {
    return (
      <figure
        data-testid={`${testIdPrefix}-single`}
        className={`relative overflow-hidden rounded-2xl border border-[var(--lssd-khaki)]/20 bg-[var(--lssd-olive)] ${height}`}
      >
        <img
          src={images[0].src}
          alt={images[0].alt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 border-t border-[var(--lssd-khaki)]/35 py-2.5 px-6 sm:px-8" style={{ backgroundColor: "#0F1A0A" }}>
          <figcaption className="text-[13px] sm:text-[14px] font-semibold text-[var(--lssd-cream)] leading-snug">
            {images[0].alt}
          </figcaption>
        </div>
      </figure>
    );
  }

  return (
    <div
      data-testid={`${testIdPrefix}-carousel`}
      className={`relative overflow-hidden rounded-2xl border border-[var(--lssd-khaki)]/20 bg-[var(--lssd-olive)] ${height} group`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label="Galleria divisione"
    >
      {/* Slide */}
      {images.map((img, i) => (
        <figure
          key={img.src}
          data-testid={`${testIdPrefix}-slide-${i}`}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
          aria-hidden={i !== index}
        >
          <img
            src={img.src}
            alt={img.alt}
            loading={i === 0 ? "eager" : "lazy"}
            className="w-full h-full object-cover pointer-events-none select-none"
            draggable={false}
          />
        </figure>
      ))}

      {/* CAPTION BAR — in fondo all'immagine, full width, sfondo pieno.
          Contiene testo (sinistra) + dots di navigazione (destra) nella stessa bar. */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 border-t border-[var(--lssd-khaki)]/35 py-3 px-6 sm:px-8 flex items-center justify-between gap-4"
        style={{ backgroundColor: "#0F1A0A" }}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[13px] sm:text-[14px] font-semibold text-[var(--lssd-cream)] leading-snug truncate">
            {images[index].alt}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              data-testid={`${testIdPrefix}-dot-${i}`}
              aria-label={`Vai alla foto ${i + 1}`}
              aria-current={i === index}
              className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[var(--lssd-khaki)]" : "w-1.5 bg-[var(--lssd-cream)]/50 hover:bg-[var(--lssd-cream)]/80"}`}
            />
          ))}
        </div>
      </div>

      {/* Frecce prev/next — z-40 sopra tutto, sempre visibili e cliccabili */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); prev(); }}
        data-testid={`${testIdPrefix}-prev`}
        aria-label="Immagine precedente"
        className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-40 h-11 w-11 rounded-full bg-[var(--lssd-olive-deep)]/90 border border-[var(--lssd-khaki)]/60 text-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki)] hover:text-[var(--lssd-olive-deep)] hover:border-[var(--lssd-khaki)] active:scale-95 flex items-center justify-center transition-all shadow-lg shadow-black/40 cursor-pointer"
      >
        <ChevronLeft className="h-5 w-5 pointer-events-none" strokeWidth={2.4} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); next(); }}
        data-testid={`${testIdPrefix}-next`}
        aria-label="Immagine successiva"
        className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-40 h-11 w-11 rounded-full bg-[var(--lssd-olive-deep)]/90 border border-[var(--lssd-khaki)]/60 text-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki)] hover:text-[var(--lssd-olive-deep)] hover:border-[var(--lssd-khaki)] active:scale-95 flex items-center justify-center transition-all shadow-lg shadow-black/40 cursor-pointer"
      >
        <ChevronRight className="h-5 w-5 pointer-events-none" strokeWidth={2.4} />
      </button>

      {/* Counter mono in alto a destra */}
      <div className="absolute top-4 right-4 z-30 font-mono text-[10px] uppercase tracking-[0.22em] font-bold bg-[var(--lssd-olive-deep)]/90 border border-[var(--lssd-khaki)]/50 text-[var(--lssd-khaki)] px-2.5 py-1 rounded-full shadow-md shadow-black/30">
        {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
      </div>
    </div>
  );
}
